import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft, FolderKanban, Calendar, Sparkles, Image as ImageIcon, Video,
  FileText, HardDrive, Save, Link as LinkIcon, X, Workflow,
} from "lucide-react";
import { toast } from "sonner";

const KIND_ICONS = { image: ImageIcon, video: Video, text: FileText, swarm: Workflow };
const TYPE_ICONS = { image: ImageIcon, video: Video, document: FileText, other: HardDrive };

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [attachOpen, setAttachOpen] = useState(false);

  const load = () => api.get(`/projects/${id}`).then((r) => {
    setData(r.data);
    setName(r.data.project.name);
    setDesc(r.data.project.description || "");
  }).catch(() => navigate("/projects"));
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    await api.patch(`/projects/${id}`, { name, description: desc });
    setEditing(false);
    toast.success("Project updated");
    load();
  };

  const detach = async (genId, fileId) => {
    await api.post(`/projects/${id}/detach`, genId ? { generation_ids: [genId] } : { file_ids: [fileId] });
    toast.success("Detached");
    load();
  };

  if (!data) return <div className="p-8 text-white/40 text-sm">Loading…</div>;
  const { project, generations, files } = data;

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <Link to="/projects" className="inline-flex items-center text-sm text-white/50 hover:text-white kr-link" data-testid="back-to-projects">
        <ArrowLeft className="h-4 w-4 mr-1" /> All projects
      </Link>

      <div className="mt-4 flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 border border-white/10 flex items-center justify-center shrink-0" style={{ color: project.color }}>
            <FolderKanban className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <div>
            <div className="kr-overline">[ PROJECT ]</div>
            {editing ? (
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-[#050505] border-white/10 font-display text-2xl h-auto py-1" data-testid="edit-name-input" />
            ) : (
              <h1 className="font-display text-4xl mt-1 tracking-tight" data-testid="project-name">{project.name}</h1>
            )}
            {editing ? (
              <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} placeholder="Description" className="mt-2 bg-[#050505] border-white/10 resize-none w-[480px] max-w-full" data-testid="edit-desc-input" />
            ) : (
              <p className="text-white/55 text-sm mt-1.5 max-w-2xl">{project.description || "No description."}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setAttachOpen(true)} variant="outline" className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="attach-btn">
            <LinkIcon className="h-4 w-4 mr-2" /> Attach assets
          </Button>
          {editing ? (
            <Button onClick={save} className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90" data-testid="save-btn"><Save className="h-4 w-4 mr-2" /> Save</Button>
          ) : (
            <Button onClick={() => setEditing(true)} variant="outline" className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="edit-btn">Edit</Button>
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5">
        <Stat label="GENERATIONS" value={generations.length} accent="#8A52FF" />
        <Stat label="FILES" value={files.length} accent="#00E5FF" />
        <Stat label="STATUS" value={project.status} accent="#34D399" />
        <Stat label="CREATED" value={new Date(project.created_at).toLocaleDateString()} accent="#FBBF24" />
      </div>

      <Tabs defaultValue="generations" className="mt-10">
        <TabsList className="bg-[#050505] border border-white/5 rounded-sm h-auto">
          <TabsTrigger value="generations" className="data-[state=active]:bg-white/10 px-4 py-1.5" data-testid="tab-generations">Generations ({generations.length})</TabsTrigger>
          <TabsTrigger value="files" className="data-[state=active]:bg-white/10 px-4 py-1.5" data-testid="tab-files">Files ({files.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="generations" className="mt-5">
          {generations.length === 0 ? (
            <EmptyState icon={Sparkles} label="No generations attached yet" />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {generations.map((g) => {
                const Icon = KIND_ICONS[g.kind] || FileText;
                return (
                  <div key={g.gen_id} className="kr-card p-3 group" data-testid={`proj-gen-${g.gen_id}`}>
                    <div className="aspect-square bg-[#050505] border border-white/5 flex items-center justify-center overflow-hidden relative">
                      {g.kind === "image" ? (
                        <img src={`${BACKEND_URL}${g.result}`} alt="" className="w-full h-full object-cover" />
                      ) : g.kind === "video" ? (
                        <video src={`${BACKEND_URL}${g.result}`} muted className="w-full h-full object-cover" />
                      ) : (
                        <div className="p-4 text-xs text-white/70 overflow-hidden line-clamp-6 text-left">{g.result?.slice(0, 200)}…</div>
                      )}
                      <button onClick={() => detach(g.gen_id, null)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 bg-black/60 border border-white/10 hover:bg-red-500/20 text-white/80 hover:text-red-300 transition" data-testid={`detach-gen-${g.gen_id}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="mt-2 text-xs truncate" title={g.prompt}>{g.prompt}</div>
                    <div className="kr-overline mt-0.5"><Icon className="h-3 w-3 inline mr-1" />{g.agent}</div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="files" className="mt-5">
          {files.length === 0 ? (
            <EmptyState icon={HardDrive} label="No files attached yet" />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {files.map((f) => {
                const Icon = TYPE_ICONS[f.type] || HardDrive;
                return (
                  <div key={f.file_id} className="kr-card p-3 group relative" data-testid={`proj-file-${f.file_id}`}>
                    <div className="aspect-square bg-[#050505] border border-white/5 flex items-center justify-center overflow-hidden">
                      {f.type === "image" ? (
                        <img src={`${BACKEND_URL}${f.url}`} alt={f.name} className="w-full h-full object-cover" />
                      ) : f.type === "video" ? (
                        <video src={`${BACKEND_URL}${f.url}`} muted className="w-full h-full object-cover" />
                      ) : (
                        <Icon className="h-10 w-10 text-white/40" />
                      )}
                    </div>
                    <div className="mt-2 text-xs truncate" title={f.name}>{f.name}</div>
                    <button onClick={() => detach(null, f.file_id)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 bg-black/60 border border-white/10 hover:bg-red-500/20 text-white/80 hover:text-red-300 transition" data-testid={`detach-file-${f.file_id}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AttachDialog open={attachOpen} onOpenChange={setAttachOpen} projectId={id} onAttached={load} attachedGenIds={generations.map((g) => g.gen_id)} attachedFileIds={files.map((f) => f.file_id)} />
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="bg-[#0A0A0E] p-5">
    <div className="kr-overline">{label}</div>
    <div className="font-display text-2xl mt-1.5 capitalize" style={{ color: accent }}>{value}</div>
  </div>
);

const EmptyState = ({ icon: Icon, label }) => (
  <div className="kr-card p-16 text-center text-white/40">
    <Icon className="h-10 w-10 mx-auto mb-3" />
    <div className="text-sm">{label}</div>
    <div className="text-xs mt-1 text-white/30">Use the "Attach assets" button to add existing ones.</div>
  </div>
);

function AttachDialog({ open, onOpenChange, projectId, onAttached, attachedGenIds, attachedFileIds }) {
  const [gens, setGens] = useState([]);
  const [files, setFiles] = useState([]);
  const [selGen, setSelGen] = useState(new Set());
  const [selFile, setSelFile] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    api.get("/generations", { params: { limit: 100 } }).then((r) => setGens(r.data.items.filter((g) => !attachedGenIds.includes(g.gen_id))));
    api.get("/files").then((r) => setFiles(r.data.items.filter((f) => !attachedFileIds.includes(f.file_id))));
    setSelGen(new Set()); setSelFile(new Set());
  }, [open]);

  const toggle = (set, setter, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };

  const attach = async () => {
    if (!selGen.size && !selFile.size) return toast.error("Pick at least one item");
    await api.post(`/projects/${projectId}/attach`, {
      generation_ids: [...selGen],
      file_ids: [...selFile],
    });
    toast.success("Attached");
    onOpenChange(false);
    onAttached();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Attach assets to project</DialogTitle>
          <DialogDescription className="text-white/50">Pick existing generations and files to bind to this project.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="gens">
          <TabsList className="bg-[#050505] border border-white/5 rounded-sm h-auto">
            <TabsTrigger value="gens" className="data-[state=active]:bg-white/10 px-3 py-1" data-testid="attach-tab-gens">Generations ({gens.length})</TabsTrigger>
            <TabsTrigger value="files" className="data-[state=active]:bg-white/10 px-3 py-1" data-testid="attach-tab-files">Files ({files.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="gens" className="mt-3 max-h-[50vh] overflow-y-auto space-y-1">
            {gens.length === 0 && <div className="text-sm text-white/40 py-6 text-center">All generations already attached or none yet.</div>}
            {gens.map((g) => (
              <label key={g.gen_id} className="flex items-center gap-3 p-2 border border-white/5 hover:bg-white/[0.02] cursor-pointer" data-testid={`attach-gen-${g.gen_id}`}>
                <Checkbox checked={selGen.has(g.gen_id)} onCheckedChange={() => toggle(selGen, setSelGen, g.gen_id)} className="border-white/30" />
                <div className="text-xs font-mono text-white/40 w-20 uppercase">{g.agent}</div>
                <div className="flex-1 text-sm truncate">{g.prompt}</div>
                <div className="kr-overline">{g.kind}</div>
              </label>
            ))}
          </TabsContent>
          <TabsContent value="files" className="mt-3 max-h-[50vh] overflow-y-auto space-y-1">
            {files.length === 0 && <div className="text-sm text-white/40 py-6 text-center">All files already attached or none yet.</div>}
            {files.map((f) => (
              <label key={f.file_id} className="flex items-center gap-3 p-2 border border-white/5 hover:bg-white/[0.02] cursor-pointer" data-testid={`attach-file-${f.file_id}`}>
                <Checkbox checked={selFile.has(f.file_id)} onCheckedChange={() => toggle(selFile, setSelFile, f.file_id)} className="border-white/30" />
                <div className="flex-1 text-sm truncate">{f.name}</div>
                <div className="kr-overline">{f.type}</div>
              </label>
            ))}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button onClick={attach} className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90" data-testid="attach-confirm-btn">
            Attach {selGen.size + selFile.size > 0 && `(${selGen.size + selFile.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
